import React, { useMemo, useState, useEffect, forwardRef,
  useImperativeHandle } from "react";
import { Trans, useTranslation } from 'react-i18next';
import {
  View,
  Text,
  StyleSheet,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import {
  IDENTIFICATION_NUMBER_MAX_LENGTH,
  isIdentificationNumberContentValid,
  sanitizeIdentificationNumberInput,
} from "../../../../../helper/identificationNumberValidation";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface SenderIdentificationStepHandle {
  submit: () => Promise<boolean>;
}

const SenderIdentificationNumber = forwardRef<SenderIdentificationStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
      idNumber: "",
  });
  const storedPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).senderPhotoIdNumber as string | null | undefined);
  const [idNumber, setIdNumber] = useState(() => {
    const raw = (useSessionStore.getState() as any).senderPhotoIdNumber as string | null | undefined;
    const cleaned = sanitizeIdentificationNumberInput(String(raw ?? ""));
    return cleaned.toUpperCase();
  });
  const selectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey);
  const selectedIdTypeLabel = useSessionStore((s: SessionState) => (s as any).selectedIdTypeLabel);
  const setSenderPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).setSenderPhotoIdNumber);

  useEffect(() => {
    const cleaned = sanitizeIdentificationNumberInput(String(storedPhotoIdNumber ?? ""));
    setIdNumber(cleaned.toUpperCase());
  }, [storedPhotoIdNumber]);

  const placeholderText = useMemo(() => {
    const baseLabel = selectedIdTypeLabel || t("US_DRIVERS_LICENSE_NUMBER");
    return `${baseLabel} ${t("IDENTIFICATION_NUMBER_SUFFIX")}`;
  }, [selectedIdTypeLabel, t]);

  const isValid =
    idNumber.trim().length > 0 && isIdentificationNumberContentValid(idNumber);
      
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const trimmed = idNumber.trim();
        const newErrors = {
          idNumber: !trimmed
            ? t("ERROR_IDENTIFICATION_NUMBER_IS_REQUIRED")
            : trimmed.length > IDENTIFICATION_NUMBER_MAX_LENGTH
              ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: IDENTIFICATION_NUMBER_MAX_LENGTH })
              : !isIdentificationNumberContentValid(idNumber)
                ? t("ID_NUMBER_INVALID_CHARS")
                : "",
        };
        setErrors(newErrors);
    
        // if any error string is non-empty
        const isValid = !Object.values(newErrors).some(Boolean);
        return isValid;
      },
    }));


  const handleManualInput = async (text: string) => {
    const cleaned = sanitizeIdentificationNumberInput(text);
    const upper = cleaned.toUpperCase();
    setIdNumber(upper);
    try { setSenderPhotoIdNumber?.(upper); } catch {}
    if (errors.idNumber) setErrors({ ...errors, idNumber: "" });
  };

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>
        <Trans i18nKey="IDENTIFICATION_NUMBER_TITLE" />
      </Text>
      <Text style={styles.subtitle}>{t("PROVIDE_YOUR_PHOTO_ID_NUMBER")}</Text>
      <FloatingTextInputComponent
        testID="senderIdentificationNumber"
        isAutoFocus={true}
        label={placeholderText}
        value={idNumber}
        onChangeText={handleManualInput}
        errorText={errors.idNumber}
        onClearError={() => setErrors((prev) => ({ ...prev, idNumber: "" }))}
        autoCapitalize="characters"
        maxLength={IDENTIFICATION_NUMBER_MAX_LENGTH}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
    // paddingRight: 100
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 400
  },
  input: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    backgroundColor: "#fff",
    padding: 20,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalTitle: { fontSize: 18, fontWeight: "700", marginBottom: 10 },
  modalText: { fontSize: 15, color: "#555" },
  modalDOB: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 10,
    marginBottom: 20,
    textAlign: "center",
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  confirmText: { color: "#fff", fontWeight: "600", fontSize: 16 },
  cancelBtn: { marginTop: 10, alignItems: "center" },
  cancelText: { fontSize: 15, color: "#666" },
});

export default SenderIdentificationNumber;